import { useEffect, useId, useState } from 'react'
import { Package } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { useAuthStore } from '@/stores/authStore'
import { useWorkspace } from '@/lib/workspace'
import { usePermissions } from '@/hooks/usePermission'
import { appendMaterial, materialKey, materialSchema, readMaterials, type JobMaterial } from '@/lib/jobMaterials'

export function JobMaterialsLog({ jobId }: { jobId: string }) {
  const user = useAuthStore(s => s.user)
  const workspace = useWorkspace()
  const { can } = usePermissions()
  if (!user || !workspace.id) return <p>Materials unavailable until your workspace is loaded.</p>
  const storageKey = materialKey(workspace.id, user.id, jobId)
  return <MaterialsLog key={storageKey} storageKey={storageKey} canEdit={can('jobs.edit')} />
}

export function MaterialsLog({ storageKey, canEdit = true, fixtures }: {
  storageKey?: string; canEdit?: boolean; fixtures?: JobMaterial[]
}) {
  const id = useId()
  const [lines, setLines] = useState<JobMaterial[]>(fixtures ?? [])
  const [readError, setReadError] = useState(false)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [cost, setCost] = useState('')
  const [supplier, setSupplier] = useState('')
  useEffect(() => {
    if (!storageKey) return
    const refresh = () => {
      try { setLines(readMaterials(storageKey)); setReadError(false) }
      catch { setReadError(true) }
    }
    refresh()
    window.addEventListener('storage', refresh)
    window.addEventListener('job-materials-updated', refresh)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener('job-materials-updated', refresh)
    }
  }, [storageKey])
  function save(e: React.FormEvent) {
    e.preventDefault()
    if (!canEdit || readError) return
    const parsed = materialSchema.safeParse({ id: crypto.randomUUID(), name, quantity: Number(quantity),
      cost: cost.trim() === '' ? undefined : Number(cost), supplier: supplier.trim() || undefined })
    if (!parsed.success) { setError(parsed.error.issues[0].message); return }
    try {
      setLines(storageKey ? appendMaterial(storageKey, parsed.data) : [...lines, parsed.data])
      setEditing(false); setName(''); setQuantity('1'); setCost(''); setSupplier(''); setError('')
    } catch { setError('Material was not saved. Browser storage is unavailable or the saved log cannot be read. Your entry is still here.') }
  }
  return <section aria-label="Materials used" className="space-y-4">
    <h3 className="font-semibold">Materials used</h3>
    <p className="text-sm" style={{ color: 'var(--midnight-text3)' }}>{fixtures ? 'Demo job · changes last until you leave this fixture.' : 'Saved in this browser for your account and this job only. Not shared with the crew or included in job costs.'}</p>
    {readError ? <p role="alert">Saved materials could not be read. Existing data has not been replaced. Reopen this job after restoring browser storage.</p> : <>
      {lines.length === 0 ? <EmptyState density="module" icon={<Package />} title="No materials logged" description="Log what you used on this job. Leave cost blank when it is not known." /> :
        <ul className="space-y-3">{lines.map(line => <li key={line.id} className="ds-tile-inset p-4 space-y-1 break-words">
          <p className="font-medium">{line.name}</p>
          <p className="text-sm">Qty {line.quantity} · {line.cost === undefined ? 'Cost not recorded' : `Line cost ${line.cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}</p>
          <p className="text-sm" style={{ color: 'var(--midnight-text3)' }}>{line.supplier || 'Supplier not recorded'}</p>
        </li>)}</ul>}
      {canEdit && !editing && <Button onClick={() => setEditing(true)}>Log material</Button>}
      {canEdit && editing && <form onSubmit={save} className="space-y-4">
        <label className="block" htmlFor={`${id}-name`}>Material<input id={`${id}-name`} className="input-field w-full mt-1" autoFocus required maxLength={160} value={name} onChange={e => setName(e.target.value)} /></label>
        <label className="block" htmlFor={`${id}-qty`}>Quantity used<input id={`${id}-qty`} className="input-field w-full mt-1" type="number" inputMode="decimal" min="0.000001" max="1000000" step="any" required value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
        <label className="block" htmlFor={`${id}-cost`}>Line cost (USD, optional)<input id={`${id}-cost`} className="input-field w-full mt-1" type="number" inputMode="decimal" min="0" max="100000000" step="0.01" value={cost} onChange={e => setCost(e.target.value)} /></label>
        <p className="text-sm" style={{ color: 'var(--midnight-text3)' }}>Total paid for this line, not the price per unit.</p>
        <label className="block" htmlFor={`${id}-supplier`}>Supplier (optional)<input id={`${id}-supplier`} className="input-field w-full mt-1" maxLength={160} value={supplier} onChange={e => setSupplier(e.target.value)} /></label>
        {error && <p role="alert">{error}</p>}
        <div className="flex gap-2"><Button type="submit">Save material</Button><Button type="button" variant="secondary" onClick={() => { setEditing(false); setError('') }}>Cancel</Button></div>
      </form>}
    </>}
  </section>
}
